import { useMutation, useQuery } from "@tanstack/react-query";
import { QueryKeyTypes } from "../../../assets/types";
import { Constants } from "../../../assets/utils/lib/constants";
import { SearchUserDto, UserDto } from "../../../services/dto";
import { IActionResponse, IActionResult } from "../../../services/proxies";
import { UserClient } from "../../../services/proxies/account/userClient";
import { ViewProps } from "./props";
import { UserChangePasswordDto } from "../../../assets/utils/lib/userChangePasswordDto";

export namespace UserService {
    const userClient = new UserClient();

    export const save = () =>
        useMutation<IActionResult<UserDto>, Error, UserDto>({
            mutationFn: async (payload: UserDto): Promise<IActionResult<UserDto>> => {
                return await userClient.saveUserAsync(payload);
            },
        });

    export const getUserQuery = (userId: string | undefined) =>
        useQuery({
            queryKey: [ViewProps.UserQueryKeys[QueryKeyTypes.Get], userId],
            queryFn: async () => await userClient.getUserAsync(userId),
            enabled: Boolean(userId) && userId !== Constants.emptyGuid,
        });

    export const gridQuery = (searchQuery: SearchUserDto) =>
        useQuery({
            queryKey: [ViewProps.UserQueryKeys[QueryKeyTypes.Grid], searchQuery],
            queryFn: async () =>
                await userClient.searchUsersAsync(searchQuery),
        });

    export const deActivateUser = () =>
        useMutation<IActionResponse, Error, string>({
            mutationFn: async (userId: string | undefined): Promise<IActionResponse> => {
                return await userClient.deActivateUserAsync(userId);
            },
        });

    export const getLookups = () =>
        useQuery({
            queryKey: [ViewProps.UserQueryKeys[QueryKeyTypes.Lookup]],
            queryFn: async (): Promise<ViewProps.UserLookupProps> =>
                await userClient.getLookupsAsync(),
        });

    export const resetPassword = () => 
        useMutation<IActionResult<{ userId: string | undefined }>, Error, { userId: string | undefined }>({
            mutationFn: async (payload: { userId: string | undefined }) => {
                return await userClient.resetPasswordAsync(payload);
            },
        });

    export const unlockUser = () =>
        useMutation<IActionResult<{ userId: string | undefined }>, Error, { userId: string | undefined }>({
            mutationFn: async (payload: { userId: string | undefined }) => {
                return await userClient.unlockUserAsync(payload);
            },
        });

    export const resendEmail = () =>
        useMutation<IActionResult<{ userId: string | undefined, returnUrl: string }>, Error, { userId: string | undefined, returnUrl: string }>({
            mutationFn: async (payload: { userId: string | undefined, returnUrl: string }) => {
                return await userClient.resendEmailAsync(payload);
            },
        });

    export const changePassword = () =>
        useMutation<IActionResult<UserChangePasswordDto>, Error, UserChangePasswordDto>({
            mutationFn: async (payload: UserChangePasswordDto | undefined) => {
                return await userClient.changePasswordAsync(payload);
            },
        });

    export const enableUser = () =>
        useMutation<IActionResult<{ userId: string | undefined }>, Error, { userId: string | undefined }>({
            mutationFn: async (payload: { userId: string | undefined}) => {
                return await userClient.enableUserAsync(payload);
            },
        });
}